import { css, html, nothing } from 'lit';
import { getBackendUrl } from '../../lib/api.js';
import { BaseComponent } from '../../lib/base-component.js';
import '../shared/image-lightbox.js';

/** The page's sections, linked from the hero's top bar. Our Art only shows when the showcase has art. */
const SECTION_LINKS = [
    { id: 'about', label: 'About' },
    { id: 'art', label: 'Our Art', showcase: true },
    { id: 'commissions', label: 'Commissions' },
    { id: 'contact', label: 'Contact' },
];

class LandingPage extends BaseComponent {
    static properties = {
        _showcaseAssets: { type: Array, state: true },
        _lightboxSrc: { type: String, state: true },
    };

    static styles = css`
        :host { display: block; }
        /* The section links and Sign In, centred together, as on the VTT marketing page. */
        .topbar {
            display: flex; justify-content: center; align-items: center; gap: 0.5rem;
            padding: 0.75rem 0; margin-bottom: 1.5rem;
        }
        .topbar .nav-link { color: rgba(255, 255, 255, 0.8); font-weight: 600; }
        .topbar .nav-link:hover { color: #fff; }
        .btn-signin { color: #fff; border: 1px solid rgba(255, 255, 255, 0.6); font-weight: 600; }
        .btn-signin:hover { background: rgba(255, 255, 255, 0.12); color: #fff; }
        .hero {
            text-align: center;
            padding: 0 1rem 3rem;
            background: linear-gradient(135deg, #1a1a2e 0%, #16213e 50%, #0f3460 100%);
            color: white;
        }
        .hero-logo {
            display: block; width: 112px; height: 112px; margin: 0 auto 1rem;
            filter: drop-shadow(0 8px 24px rgba(0, 0, 0, 0.35));
        }
        .hero h1 { font-size: 2.5rem; font-weight: 700; margin-bottom: 0.75rem; }
        .hero .tagline {
            font-size: 1.15rem; color: #b8c6db; max-width: 600px;
            margin: 0 auto 2rem; line-height: 1.6;
        }
        .hero .btn-cta { font-size: 1.1rem; padding: 0.75rem 2.5rem; border-radius: 2rem; }
        .section { max-width: 900px; margin: 0 auto; padding: 2rem 1.5rem; }
        .section-tight { padding-bottom: 0.5rem; }
        .section h2 { font-size: 1.75rem; font-weight: 600; margin-bottom: 1rem; }
        .section p { font-size: 1rem; color: #555; line-height: 1.7; }
        .asset-types {
            display: grid; grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
            gap: 1.5rem; margin-top: 2rem;
        }
        .asset-card {
            border: 1px solid #e9ecef; border-radius: 12px; padding: 1.5rem; text-align: center;
        }
        .asset-card i { font-size: 2rem; color: #0d6efd; margin-bottom: 0.75rem; display: block; }
        .asset-card h3 { font-size: 1.1rem; font-weight: 600; margin-bottom: 0.5rem; }
        .asset-card p { font-size: 0.85rem; color: #888; margin: 0; }
        .showcase-section { background: #f8f9fa; padding: 2rem 1.5rem; text-align: center; }
        .showcase-section h2 { font-size: 1.75rem; font-weight: 600; margin-bottom: 0.5rem; }
        .showcase-section .subtitle { color: #888; margin-bottom: 2rem; }
        .showcase-grid {
            display: flex; gap: 1.5rem; justify-content: center; flex-wrap: wrap;
            max-width: 900px; margin: 0 auto;
        }
        .showcase-item {
            width: 160px; height: 160px; border-radius: 12px; background: #e9ecef;
            display: flex; align-items: center; justify-content: center; overflow: hidden;
            cursor: pointer;
        }
        .showcase-item img { width: 100%; height: 100%; object-fit: contain; transition: opacity 0.15s; }
        .showcase-item:hover img { opacity: 0.8; }
        .cta-section {
            text-align: center; padding: 3rem 1.5rem; max-width: 600px; margin: 0 auto;
        }
        .cta-section h2 { font-size: 1.75rem; font-weight: 600; margin-bottom: 0.75rem; }
        .cta-section p { color: #555; margin-bottom: 1.5rem; line-height: 1.6; }
        .footer {
            text-align: center; padding: 1.5rem; color: #aaa; font-size: 0.8rem;
            border-top: 1px solid #eee;
        }
        .footer .credit { margin-top: 0.35rem; font-size: 0.75rem; }
        .footer .credit a { color: inherit; }
    `;

    constructor() {
        super();
        this._showcaseAssets = [];
        this._lightboxSrc = '';
    }

    async connectedCallback() {
        super.connectedCallback();
        this._loadShowcase();
    }

    async _loadShowcase() {
        try {
            const resp = await fetch(`${getBackendUrl()}/api/public/showcase`);
            if (resp.ok) {
                const data = await resp.json();
                this._showcaseAssets = (data.assets || []).filter((a) => a.image_url);
            }
        } catch {
            // Landing page works without showcase
        }
    }

    _handleLogin() {
        this.dispatchEvent(new CustomEvent('login', { bubbles: true, composed: true }));
    }

    _scrollTo(id) {
        this.renderRoot.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
    }

    _renderTopbar() {
        const links = SECTION_LINKS.filter(
            (link) => !link.showcase || this._showcaseAssets.length > 0,
        );
        return html`
            <nav class="topbar">
                <div class="d-none d-md-flex">
                    ${links.map(
                        (link) => html`
                            <button class="btn nav-link px-3" type="button" @click="${() => this._scrollTo(link.id)}">
                                ${link.label}
                            </button>
                        `,
                    )}
                </div>
                <button class="btn btn-signin rounded-pill btn-sm px-3" type="button" @click="${this._handleLogin}">Sign In</button>
            </nav>
        `;
    }

    render() {
        return html`
            <div class="hero">
                ${this._renderTopbar()}
                <img class="hero-logo" src="/assets-logo.svg" alt="">
                <h1>Dice Driven Assets</h1>
                <p class="tagline">
                    We commission freelance artists to create beautiful art for our digital tabletop games and tools.
                    If you're a digital artist who loves fantasy illustration, we'd love to work with you.
                </p>
            </div>

            <div class="section section-tight" id="about">
                <h2>What We Build</h2>
                <p>
                    Dice Driven Design builds RPGish, a cross-platform tabletop RPG that brings automated,
                    GM-less gameplay to Steam, mobile, and web. We use the 5e System Reference Document
                    and need high-quality art assets to bring our game to life.
                </p>
            </div>

            ${
                this._showcaseAssets.length > 0
                    ? html`
                <div class="showcase-section" id="art">
                    <h2>Our Art</h2>
                    <p class="subtitle">A selection of commissioned artwork from our artists</p>
                    <div class="showcase-grid">
                        ${this._showcaseAssets.map(
                            (a) => html`
                            <div class="showcase-item" @click="${() => (this._lightboxSrc = a.image_url)}">
                                <img src="${a.image_url}" alt="${a.slug}">
                            </div>
                        `,
                        )}
                    </div>
                </div>
            `
                    : nothing
            }

            <div class="section" id="commissions">
                <h2>What We Commission</h2>
                <div class="asset-types">
                    <div class="asset-card">
                        <i class="bi bi-person-circle"></i>
                        <h3>Miniature Art</h3>
                        <p>Character and monster miniatures for the game grid. Top-down perspective, clean edges, transparent backgrounds.</p>
                    </div>
                    <div class="asset-card">
                        <i class="bi bi-map"></i>
                        <h3>Map Tiles</h3>
                        <p>Dungeon, wilderness, and interior map tiles that combine into explorable environments.</p>
                    </div>
                    <div class="asset-card">
                        <i class="bi bi-palette"></i>
                        <h3>UI Art</h3>
                        <p>Icons, frames, and decorative elements for the game interface.</p>
                    </div>
                </div>
            </div>

            <div class="cta-section" id="contact">
                <h2>Interested?</h2>
                <p>
                    We offer competitive per-commission rates, clear briefs with concept sketches,
                    and a structured review process. If you're interested in working with us,
                    reach out at <a href="mailto:art@dicedriven.com">art@dicedriven.com</a>.
                </p>
            </div>

            <image-lightbox .src="${this._lightboxSrc}" @close="${() => (this._lightboxSrc = '')}"></image-lightbox>

            <div class="footer">
                &copy; ${new Date().getFullYear()} Dice Driven Design LLC. All rights reserved.
                <div class="credit">
                    Logo icon: Art by Muhammad Naufal Subhiansyah from
                    <a href="https://thenounproject.com/browse/icons/term/art/" target="_blank" rel="noopener">Noun Project</a> (CC BY 3.0)
                </div>
            </div>
        `;
    }
}

customElements.define('landing-page', LandingPage);
